import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Chip, Container, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import Workspace, { CaseCreationTypes, Colors, CountryList, CreatorTypes, WorkspaceMainFileType } from '../../models/Workspace';
import { megaBytesToBytes } from '../../utils/files';
import EditFields from '../common/EditFields';
import LoadingButton from '../common/LoadingButton';

const useStyles = makeStyles((theme) => ({
  cancelButton: {
    '&:hover': {
      boxShadow: `0 0 0 1px ${theme.palette.primary.main} inset`,
    },
    backgroundColor: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main} inset`,
    color: theme.palette.primary.main,
  },
  chipComponent: {
    margin: '4px',
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  gridItem: {
    '& .MuiInputBase-input': {
      height: '2rem !important',
    },
    paddingLeft: '16px',
    paddingRight: '16px',
    position: 'relative',
  },
  label: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(3),
  },
  submitContainer: {
    justifyContent: 'flex-end',
    paddingBottom: theme.spacing(3),
    position: 'absolute',
    right: 0,
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(3),
    },
  },
  subtitle1: {
    fontSize: 16,
    paddingTop: theme.spacing(2),
  },
  titleContainer: {
    padding: theme.spacing(4),
  },
}));

export default ({
  onSubmit,
  onRequestClinicCode,
  validationErrors,
  onCancel,
  onRejectClinicCode,
  clinicCode,
  isSubmitting$,
  isRequestingCode$,
  ambraNamespaces = [],
  onRemoveValidationErrorsByKey,
}) => {
  const classes = useStyles();
  const firstGroupRef = useRef();
  const secondGroupRef = useRef();
  const thirdGroupRef = useRef();
  // const fourthGroupRef = useRef();
  const fifthGroupRef = useRef();
  const [defaultData, setDefaultData] = useState(new Workspace());
  const [selectedProductType, setSelectedProductType] = useState('law-firm');

  const handleNameChange = (name) => {
    if (name || name === '') {
      const subdomain = name.toLowerCase().replaceAll(' ', '-');
      thirdGroupRef.current.setValue('subdomain', subdomain);
    }

    if (name?.length >= 3) {
      onRequestClinicCode(name);
    } else onRejectClinicCode();
  };

  const handleRemoveValidationErrors = useCallback(
    (key) => {
      if (onRemoveValidationErrorsByKey) onRemoveValidationErrorsByKey(key);
    },
    [onRemoveValidationErrorsByKey]
  );

  const handleCancelClick = () => {
    onCancel();
  };
  const fieldGroup1 = useMemo(
    () => [
      {
        label: 'Workspace Name',
        maxLength: 32,
        name: 'name',
        onChange: (name) => {
          handleNameChange(name);
          handleRemoveValidationErrors('subdomain');
        },
        placeholder: 'Workspace name',
        required: true,
        type: 'text',
      },
      {
        items: CaseCreationTypes,
        label: 'Case Creation',
        name: 'caseCreationType',
        onChange: () => handleRemoveValidationErrors('caseCreationType'),
        required: true,
        type: 'select',
      },
      { items: CreatorTypes, label: 'Creators', name: 'creatorType', onChange: () => handleRemoveValidationErrors('creatorType'), required: true, type: 'select' },
      { label: 'Clinic Address', name: 'address', onChange: () => handleRemoveValidationErrors('address'), placeholder: '11 Street Name, Area', required: true, type: 'text' },
      { items: CountryList, label: 'Country', name: 'countryCode', onChange: () => handleRemoveValidationErrors('countryCode'), required: true, type: 'select' },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleRemoveValidationErrors]
  );

  const fieldGroup2 = useMemo(
    () => [
      {
        label: 'Clinic Contact Number',
        name: 'contactNumber',
        onChange: () => handleRemoveValidationErrors('contactNumber'),
        placeholder: '+27828501512',
        required: true,
        type: 'phone',
      },
      {
        label: 'Administrator Name',
        name: 'adminName',
        onChange: () => handleRemoveValidationErrors('adminName'),
        placeholder: 'Administrator name',
        required: true,
        type: 'text',
      },
      {
        label: 'Administrator Telephone',
        name: 'adminTelephone',
        onChange: () => handleRemoveValidationErrors('adminTelephone'),
        placeholder: '+27828501512',
        required: true,
        type: 'phone',
      },
      {
        label: 'Administrator Email Address',
        name: 'adminEmail',
        onChange: () => handleRemoveValidationErrors('adminEmail'),
        placeholder: 'email@address.com',
        required: true,
        type: 'text',
      },
    ],
    [handleRemoveValidationErrors]
  );

  const fieldGroup3 = useMemo(() => {
    const baseFields = [
      {
        errorMessage: 'Only images files not bigger than 2MB are accepted.',
        label: 'Workspace Logo',
        message: 'Max height: 52px (recommended) . Max size allowed: 2MB',
        name: 'logoUrl',
        onChange: () => handleRemoveValidationErrors('logoUrl'),
        required: true,
        type: 'image-upload',
        validation: (file) => file && file.size < megaBytesToBytes(2),
      },
      // Only show color picker if workspace type is not tech-qa
      ...(selectedProductType !== 'tech-qa' ? [{ items: Colors, label: 'Workspace Primary colour', name: 'primaryColor', required: true, type: 'color-select' }] : []),
      { label: 'Subdomain', name: 'subdomain', onChange: () => handleRemoveValidationErrors('subdomain'), placeholder: 'clinicname', required: true, type: 'subdomain' },
    ];

    return baseFields;
  }, [handleRemoveValidationErrors, selectedProductType]);

  const fieldGroup5 = useMemo(
    () => [
      {
        className: 'custom-height force',
        items: ambraNamespaces,
        label: 'Linked Namespaces',
        multiple: true,
        name: 'namespaces',
        renderValue: (selected) => {
          const chips = selected.map((sl) => {
            const data = ambraNamespaces.filter((aN) => aN.value === sl);
            return <Chip className={classes.chipComponent} size="small" variant="outlined" key={sl} label={data[0].label} />;
          });
          return <div className={classes.chipsContainer}>{chips}</div>;
        },
        required: true,
        type: 'select',
      },

      // Only existing workspaces can edit this, so it won't appear on the creation form
      // {
      //   className: 'custom-height force',
      //   items: [
      //     { label: 'Yes', value: 'true' },
      //     { label: 'No', value: 'false' },
      //   ],
      //   label: 'Can Request Before Pay',
      //   multiple: false,
      //   name: 'canRequestBeforePay',
      //   required: true,
      //   type: 'select',
      // },

      // {
      //   className: 'custom-height force',
      //   items: [
      //     { label: 'Enable', value: 'true' },
      //     { label: 'Disable', value: 'false' },
      //   ],
      //   label: 'Creator Mode',
      //   multiple: false,
      //   name: 'creatorMode',
      //   required: true,
      //   type: 'select',
      // },

      {
        className: 'custom-height force',
        items: [
          // { label: 'White Glove+', value: 'white-glove-plus' },
          // { label: 'White Glove', value: 'white-glove' },
          { label: 'Law Firm', value: 'law-firm' },
          { label: 'Tech QA', value: 'tech-qa' },
        ],
        label: 'Workspace Product Type',
        multiple: false,
        name: 'productType',
        onChange: (value) => {
          setSelectedProductType(value.productType);
        },
        required: true,
        type: 'select',
      },

      ...(selectedProductType === 'law-firm'
        ? [
            {
              className: 'custom-height force',
              items: [WorkspaceMainFileType.Any, WorkspaceMainFileType.Dicom],
              label: 'Workspace Main File Type',
              multiple: false,
              name: 'mainFileType',
              required: true,
              type: 'select',
            },

            {
              className: 'custom-height force',
              items: [
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ],
              label: 'Radiology Report Required',
              multiple: false,
              name: 'radiologyReportRequired',
              required: true,
              type: 'select',
            },
          ]
        : []),
    ],
    [ambraNamespaces, classes, selectedProductType]
  );

  useEffect(() => {
    if (selectedProductType === 'tech-qa') {
      if (thirdGroupRef.current) {
        thirdGroupRef.current.setValue('primaryColor', '#2AAAE2');
      }
    }
  }, [selectedProductType]);

  const handleSubmit = async () => {
    const data = await Promise.all([
      firstGroupRef.current.getValidData(),
      secondGroupRef.current.getValidData(),
      thirdGroupRef.current.getValidData(),
      fifthGroupRef.current.getValidData(),
    ]);

    if (!data.some((groupData) => !groupData)) {
      const dataObject = {};
      data.forEach((groupData) => {
        Object.assign(dataObject, groupData);
      });

      Object.assign(dataObject, { allowClinicUpload: dataObject.allowClinicUpload === 'allowed', clinicCode });
      Object.assign(dataObject, { namespaceIds: dataObject.namespaces });
      if (dataObject.namespaces) delete dataObject.namespaces;

      onSubmit(dataObject);
    }
  };
  return (
    <Container component="main">
      <Container component="div" xs={8} className={classes.titleContainer}>
        <Typography variant="h4" align="left" className="page-header">
          New Workspace
        </Typography>
        <Typography variant="subtitle1" align="left" style={{ maxWidth: 270 }} className="body-copy">
          Create a new workspace and assign an administrator to manage the workspace.
        </Typography>
      </Container>

      <form className={classes.form}>
        <Grid container justify="space-around">
          <Grid item xs={12} sm={4} className={classes.gridItem}>
            <EditFields
              validationErrors={validationErrors}
              ref={firstGroupRef}
              fields={fieldGroup1}
              data={defaultData}
              labelPosition="top"
              labelClass={classes.label}
              validate={Workspace.validateCreate}
            />
          </Grid>
          <Grid item xs={12} sm={4} className={classes.gridItem}>
            <EditFields
              validationErrors={validationErrors}
              ref={secondGroupRef}
              fields={fieldGroup2}
              data={defaultData}
              labelPosition="top"
              labelClass={classes.label}
              useAutoFocus={false}
              validate={Workspace.validateCreate}
            />
            <Typography variant="subtitle1" align="left" className={classes.subtitle2}>
              Note that an invitation link will be sent to the administrator email address once the workspace is created
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} className={classes.gridItem}>
            <div className="pb-2">
              <EditFields
                validationErrors={validationErrors}
                ref={thirdGroupRef}
                fields={fieldGroup3}
                data={defaultData}
                labelPosition="top"
                labelClass={classes.label}
                useAutoFocus={false}
                validate={Workspace.validateCreate}
              />
              <EditFields
                validationErrors={validationErrors}
                ref={fifthGroupRef}
                fields={fieldGroup5}
                data={defaultData}
                labelPosition="top"
                labelClass={classes.label}
                useAutoFocus={false}
                validate={Workspace.validateCreate}
              />
            </div>
            <div style={{ height: '16px' }} />
            <Grid container direction="row" className={classes.submitContainer}>
              <Grid item xs={6} className={classes.gridItem}>
                <Button variant="contained" className={classes.cancelButton} onClick={handleCancelClick} fullWidth>
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6} className={classes.gridItem}>
                <LoadingButton variant="contained" color="primary" fullWidth onClick={handleSubmit} loading$={isSubmitting$ || isRequestingCode$}>
                  Create Workspace
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};
